import Icon from '../../../Assets/Icon/CustomIcons';
import { GridCheckBoxFunction } from './GridCheckBoxFunction';

// 한 번의 드래그가 여러 셀 렌더러를 지나가므로 컴포넌트 밖에서 세션을 공유한다.
let dragSelectionSession = null;

// GridCheckBox 컴포넌트 - AG Grid 안에 'no'컬럼에 들어가는 체크박스 및 삭제 복원 버튼
// - 삭제 상태인 행: 복원 버튼
// - 일반 행: 체크박스
// - Shift+클릭 등 다중 선택 및 선택 상태 관리를 외부 ref로 유지
// - 외부 이벤트 전파 차단을 통해 의도치 않은 행 선택 방지
// - 사용 예시:
//   columnDefs: [
//     {
//       field: 'no',
//       headerName: 'NO',
//       cellRenderer: (params) => (
//         <GridCheckBox
//           params={params}                              //params: AG Grid가 셀 렌더러에 전달하는 정보 객체
//           DeleteNewRow={DeleteNewRow}                  //DeleteNewRow: 추가된 행을 삭제할 때 호출되는 함수
//           RestoreRow={RestoreRow}                      //RestoreRow: 삭제 상태인 행을 복원할 때 호출되는 함수
//           gridRef={gridRef}                            //gridRef : AG Grid의 ref 객체
//           rowData={rowData}                            //rowData: 현재 그리드에 표시 중인 전체 row 데이터 배열
//           selectedRowNosRef={selectedRowNosRef}        //selectedRowNosRef: 선택된 행 번호들을 담는 ref 객체
//           lastCheckedIndexRef={lastCheckedIndexRef}    //lastCheckedIndexRef: 마지막으로 체크한 인덱스를 담는 ref 객체 (shift+클릭으로 다중선택 지원)
//           handleRowSelection={handleRowSelection}      //handleRowSelection: 체크박스 클릭 시 실행할 함수
//         />
//       ),
//     },
//   ];

export default function GridCheckBox({
  params,
  RestoreRow,
  DeleteNewRow,
  gridRef,
  rowData,
  selectedRowNosRef,
  lastCheckedIndexRef,
  handleRowSelection,
  preferNodeRowData,
  hideCheckboxCondition,
  enableDragSelection,
}) {
  const rowNo = params.data?.no;
  const isDeleted = params.data?.deleteYn === 'Y';
  const isNew = params.data?.newYn === 'Y';
  const rowIndex = params.rowIndex;
  const shouldHideCheckbox = hideCheckboxCondition?.(params) === true;

  const applyDragSelection = (selected) => {
    const gridApi = gridRef?.current?.api;
    if (!gridApi || params.node?.displayed === false) return;

    if (selected) {
      selectedRowNosRef?.current?.add(rowNo);
    } else {
      selectedRowNosRef?.current?.delete(rowNo);
    }

    if (params.node?.isSelected?.() !== selected) {
      params.node?.setSelected?.(selected, false, 'checkboxDrag');
    }
    if (lastCheckedIndexRef) lastCheckedIndexRef.current = rowIndex;
  };

  const beginDragSelection = (e) => {
    if (!enableDragSelection || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    const selected = !params.node?.isSelected?.();
    dragSelectionSession = {
      api: gridRef?.current?.api,
      selected,
    };
    applyDragSelection(selected);
  };

  const continueDragSelection = (e) => {
    if (
      !enableDragSelection ||
      !dragSelectionSession ||
      dragSelectionSession.api !== gridRef?.current?.api ||
      (e.buttons & 1) !== 1
    ) {
      if ((e.buttons & 1) !== 1) dragSelectionSession = null;
      return;
    }
    e.preventDefault();
    applyDragSelection(dragSelectionSession.selected);
  };

  if (rowNo == null) return null;

  if (shouldHideCheckbox) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.7rem',
        }}
      >
        <span>{rowNo}</span>
      </div>
    );
  }

  if (isDeleted) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.7rem',
        }}
      >
        <button
          style={{
            color: '#cf1b1b',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '0.15rem',
            border: '1.4px solid #cf1b1b',
            backgroundColor: 'transparent',
          }}
          onClick={(e) => {
            e.stopPropagation();
            RestoreRow(params.data);
          }}
        >
          <Icon.Minus width={18} height={18} />
        </button>
        <span>{rowNo}</span>
      </div>
    );
  }
  if (isNew) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.7rem',
        }}
      >
        <button
          style={{
            color: '#f44336',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: 'none',
            backgroundColor: 'transparent',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setTimeout(() => {
              document.activeElement?.blur();
              gridRef.current?.api?.clearFocusedCell();
              DeleteNewRow(params.data);
            }, 0);
          }}
        >
          <Icon.Delete width={16} height={16} />
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.7rem',
        width: '100%',
        height: '100%',
        cursor: enableDragSelection ? 'pointer' : 'default',
        userSelect: 'none',
      }}
      onMouseDown={beginDragSelection}
      onMouseEnter={continueDragSelection}
      onMouseUp={() => {
        dragSelectionSession = null;
      }}
      onClick={(e) => {
        if (!enableDragSelection) return;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <input
        type="checkbox"
        style={{
          width: '20px',
          height: '20px',
          pointerEvents: enableDragSelection ? 'none' : 'auto',
        }}
        {...(preferNodeRowData
          ? {
              'data-row-key':
                params.node?.id != null
                  ? String(params.node.id)
                  : rowNo != null
                    ? String(rowNo)
                    : `idx-${rowIndex}`,
            }
          : {})}
        checked={params.node.isSelected()}
        onChange={
          enableDragSelection
            ? undefined
            : GridCheckBoxFunction({
                params,
                rowIndex,
                rowData,
                gridRef,
                selectedRowNosRef,
                lastCheckedIndexRef,
                handleRowSelection,
                preferNodeRowData,
              })
        }
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      <span>{rowNo}</span>
    </div>
  );
}
